import React from 'react';
import { Box, Typography, Button, LinearProgress, Chip } from '@mui/material';
import { Close, Error as ErrorIcon } from '@mui/icons-material';
import { PusherConnectionState } from '../types';

interface ModalHeaderProps {
  error: string;
  isCompleted: boolean;
  stateCleared: boolean;
  progress: number;
  pusherConnectionState: PusherConnectionState;
  fallbackPollingActive: boolean;
  jobId?: string;
  onClose?: () => void;
  onCloseClick: () => void;
  theme?: 'dark' | 'light';
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  error,
  isCompleted,
  stateCleared,
  progress,
  pusherConnectionState,
  fallbackPollingActive,
  jobId,
  onClose,
  onCloseClick,
  theme = 'dark',
}) => {

  const isLight = theme === 'light';
  const headerBg = error ? 'rgba(185, 28, 28, 0.88)' : (isLight ? '#ffffff' : 'rgba(8, 15, 24, 0.88)');
  const headerColor = error ? '#ffffff' : (isLight ? '#0b1220' : '#f8fafc');
  const subTextColor = error ? 'rgba(255, 255, 255, 0.8)' : (isLight ? 'rgba(0, 0, 0, 0.7)' : 'rgba(226, 232, 240, 0.78)');
  const closeColor = isLight ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)';
  const closeHoverBg = isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)';
  const progressTrack = isLight ? '#e5e7eb' : 'rgba(148, 163, 184, 0.24)';
  const progressCaption = isLight ? 'rgba(0, 0, 0, 0.6)' : 'rgba(191, 219, 254, 0.82)';
  
  
  return (
    <Box sx={{ 
      bgcolor: headerBg,
      color: headerColor,
      p: 3,
      position: 'relative',
      backdropFilter: isLight ? 'none' : 'blur(16px)'
    }}>
      {/* Close Button - only enabled when completed/error AND state is cleared */}
      {onClose && (isCompleted || error) && stateCleared && (
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Button
            onClick={onCloseClick}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              borderRadius: 1,
              color: closeColor,
              '&:hover': {
                backgroundColor: closeHoverBg,
                color: headerColor
              }
            }}
          >
            <Close fontSize="small" />
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        {error && (
          <ErrorIcon sx={{ color: 'white', fontSize: '1.5rem' }} />
        )}
        <Typography 
          variant="h6" 
          component="h2" 
          sx={{ 
            fontWeight: 600,
            color: headerColor
          }}
        >
          {error ? 'Processing Failed' : 'Processing Documents'}
        </Typography>
        
        {/* Connection Status Indicator */}
        {!error && !isCompleted && (
          <Chip
            label={
              pusherConnectionState === 'connected' ? 'Live Updates' :
              pusherConnectionState === 'connecting' ? 'Connecting...' :
              fallbackPollingActive ? 'Backup Mode' : 'Reconnecting...'
            }
            size="small"
            sx={{
              backgroundColor: 
                pusherConnectionState === 'connected' ? '#dcfce7' :
                fallbackPollingActive ? '#fef3c7' : '#fde8e8',
              color: 
                pusherConnectionState === 'connected' ? '#166534' :
                fallbackPollingActive ? '#92400e' : '#991b1b',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: 20,
              border: `1px solid ${
                pusherConnectionState === 'connected' ? '#bbf7d0' :
                fallbackPollingActive ? '#fde68a' : '#fecaca'
              }`
            }}
          />
        )}
      </Box>
      
      <Typography 
        variant="body2" 
        sx={{ 
          color: subTextColor,
          fontSize: '0.875rem'
        }}
      >
        {error 
          ? 'An error occurred while processing your documents'
          : 'Live progress and results will appear here inside the current tab'
        }
      </Typography>



      {/* Progress Bar - only show if not completed and no error */}
      {!isCompleted && !error && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant={progress > 0 ? "determinate" : "indeterminate"}
            value={progress}
            sx={{
              height: 3,
              borderRadius: 1.5,
              backgroundColor: progressTrack,
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'hsl(var(--accent))',
                borderRadius: 1.5,
              }
            }}
          />
          {progress > 0 && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: progressCaption,
                mt: 0.5,
                display: 'block',
                textAlign: 'right',
                fontSize: '0.75rem'
              }}
            >
              {Math.round(progress)}% complete
            </Typography>
          )}
        </Box>
      )}

      {/* Error state indicator */}
      {error && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{
            height: 3,
            borderRadius: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              borderRadius: 1.5,
            }} />
          </Box>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              mt: 0.5,
              display: 'block',
              textAlign: 'center',
              fontSize: '0.75rem'
            }}
          >
            Processing stopped due to error
          </Typography>
        </Box>
      )}
    </Box>
  );
}; 